/**
 * SdkConsentDialog — thin React wrapper over the framework-agnostic
 * `mountConsentDialog` (@papan/sdk/consent), which is the SINGLE source of
 * truth shared with the ppn.gg App-Mode consent popup. Keeping one renderer
 * is the whole point: the markup + decision logic + POST contract can't drift
 * between the two surfaces.
 *
 * The dialog is mounted OUTSIDE the preview iframe (the HTML author's CSS / JS
 * cannot touch it): the parent SPA renders this overlay over the iframe via a
 * high-z `position:fixed` layer. It records the decision via
 * `POST /api/v1/sdk/consent` before resolving. Per v1 plan §789 +
 * sdk-scenarios/user-flow.html.
 *
 * The author-supplied iframe never reaches this component — only the
 * server-authoritative 412 body strings flow in (R21 #140 brand-spoof).
 */
import { useEffect, useRef } from 'react'
import { mountConsentDialog, type ConsentDecision } from '@papan/sdk/consent'
import { getDefaultHeaders, getApiBase } from '@papan/api-client'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'

export interface SdkConsentRequest {
  appFolderId: string
  appName: string
  appOwnerName: string | null
  required: string[]
  optional: string[]
  /** Current grants — pre-populates the toggles when managing consent. */
  granted?: string[]
  /** Current refusals. A declined field is a decision on record and gets a row
   *  like any other; dropping it here made it invisible and unrevisitable. */
  declined?: string[]
  /** Fields asked about for the FIRST time. Load-bearing, not informational:
   *  the dialog reads a known record WITHOUT this as "management view", so
   *  dropping it turned every gate rendered through this wrapper into a
   *  Cancel / Save screen with no Decline — on an app waiting for consent. */
  newFields?: string[]
  resolve: (decision: ConsentDecision | null) => void
}

/** Share-view consent routing. When present, the dialog writes consent FIRST-PARTY
 *  via `/api/v1/accounts/ppn-consent` (the recipient's Better Auth session) instead of
 *  `/api/v1/sdk/consent` — which rejects the share-session principal the share-view
 *  otherwise sends (`consent_must_be_first_party`). The acting identity + share token
 *  ride the body so the endpoint keys the grant on the picked org and resolves the
 *  link grant for its canRead gate. */
export interface ShareConsentRouting {
  tenantId: string | null
  email: string | null
  shareToken: string | null
  /** The visitor's share SESSION — the server keys the consent on its STORED pair and
   *  gates it as the session resolves (verified link email + native half + admission). */
  shareSession?: string | null
}

interface Props {
  request: SdkConsentRequest
  onClose: () => void
  /** Set in the share-view to route the write first-party (see ShareConsentRouting). */
  shareRouting?: ShareConsentRouting | null
}

export function SdkConsentDialog({ request, onClose, shareRouting }: Props) {
  // onClose is a fresh closure each render; read it through a ref so the
  // dialog isn't re-mounted on every parent render. `request` is stable
  // state in the parent, so it's a safe re-mount key.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  // Portaled to <body>, outside every ThemedArea: the dialog carries the SPA's theme class itself
  // (its glass reads theme-dim / theme-light from it).
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const themeClassRef = useRef(themeClass)
  themeClassRef.current = themeClass
  // A theme switch while it is open recolours it in place — never a remount (that would drop the
  // switches the person has already flipped).
  useEffect(() => { if (containerRef.current) containerRef.current.className = themeClass }, [themeClass])

  useEffect(() => {
    // R15 — clickjack guard covering ALL three consent paths (manage / proactive / 412)
    // that mount this dialog. A grant must never be collectable while the SPA is framed
    // (an attacker frames the app + overlays bait over "Allow"); the legit previewer is
    // always top-level. Resolve as a dismiss so the gate flow doesn't hang. (Broad SPA-wide
    // X-Frame-Options is the stronger fix — tracked in #181.)
    if (typeof window !== 'undefined' && window.top !== window.self) {
      request.resolve(null)
      onCloseRef.current()
      return
    }
    // Mount the overlay host at the document root, NOT inline in the preview
    // subtree. The workspace editor card (.ws-card-hero) is a
    // `position:relative; z-index:1` stacking context, which traps the
    // overlay's `position:fixed; z-index:9999` at layer 1 — the ChatPanel
    // column (later DOM, same z-1 context) then paints over it. Appending to
    // <body> lets z-index:9999 compete at the document root, matching how the
    // sibling dialogs (MiniAppConsentWizard etc.) portal out.
    const container = document.createElement('div')
    container.className = themeClassRef.current
    containerRef.current = container
    document.body.appendChild(container)
    const cleanup = mountConsentDialog(
      container,
      {
        appFolderId: request.appFolderId,
        appName: request.appName,
        appOwnerName: request.appOwnerName,
        required: request.required,
        optional: request.optional,
        granted: request.granted,
        declined: request.declined,
        newFields: request.newFields,
      },
      {
        // Split-origin (#234): the consent write must hit the API origin —
        // same-origin (the SDK default, correct for the ppn.gg BFF) has no
        // session cookie on split envs.
        baseUrl: getApiBase(),
        onResolve: (decision) => {
          request.resolve(decision)
          onCloseRef.current()
        },
        // Share-view: write FIRST-PARTY via /accounts/ppn-consent (the share-session
        // principal that getDefaultHeaders() would inject is rejected by /sdk/consent).
        // Send NO share header — the endpoint authenticates on the recipient's Better
        // Auth cookie (credentials:'include') + same-origin, and keys the grant on the
        // acting org identity carried in the body.
        ...(shareRouting
          ? {
              endpoint: '/api/v1/accounts/ppn-consent',
              headers: { },
              extraBody: {
                ...(shareRouting.tenantId ? { tenantId: shareRouting.tenantId } : {}),
                ...(shareRouting.email ? { email: shareRouting.email } : {}),
                ...(shareRouting.shareToken ? { appToken: shareRouting.shareToken } : {}),
                ...(shareRouting.shareSession ? { session: shareRouting.shareSession } : {}),
              },
            }
          : {
              // R15 — forward the active-tenant headers so the consent WRITE keys on the same
              // tenant the reads use. The in-host path previously omitted them and silently wrote
              // org-workspace grants to the user's personal tenant ("Allow does nothing").
              headers: getDefaultHeaders(),
            }),
      },
    )
    return () => {
      cleanup()
      container.remove()
    }
  }, [request, shareRouting])

  return null
}
